import { PositionCategoryKind, UserCategoryKind } from '@revelio/data-access';
import type { SelectionList, ValidValueTypes } from '@revelio/iso-utility';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../../../../../shared/selection-lists/types';
import { STATIC_FILTER_DATA } from '../../constants/staticFilters';
import type { FilterDataResult, TreeData } from '../../contexts/FilterProvider';
import { getFilterDefinition } from '../../engine/filterRegistry';
import { type FilterId, FilterType } from '../../types/filters';

/**
 * These helpers are used while converting AI expression leaves into CustomDash selections.
 *
 * The AI usually returns *numeric* ids (e.g. companyId=233459). CustomDash tree filters often
 * expect a *path-like* id (e.g. "14_60_233459") so we try to resolve that using already-loaded
 * filter data (`getFilterData`) and fall back to selection lists if needed.
 */
export const resolveSelectionListLabelById = ({
  selectionListId,
  numericId,
  rawId,
  selectionLists,
}: {
  selectionListId: string;
  numericId: number;
  rawId?: string;
  selectionLists: SelectionList<ValidValueTypes>[];
}): string | null => {
  // Best-effort label lookup from selection lists (what the UI uses for display).
  const list = selectionLists.find((l) => l.id === selectionListId);
  const exactItem =
    rawId === undefined
      ? undefined
      : list?.value.find((v) => String(v.id) === rawId);
  const item =
    exactItem ??
    list?.value.find((v) => {
      const id = String(v.id);
      const leafId = id.split('_').pop();
      return id === String(numericId) || leafId === String(numericId);
    });
  if (!item) return null;

  const longName = (item as { longName?: unknown }).longName;
  const shortName = (item as { shortName?: unknown }).shortName;
  if (typeof longName === 'string' && longName.trim()) return longName;
  if (typeof shortName === 'string' && shortName.trim()) return shortName;

  return typeof item.id === 'string' ? item.id : String(item.id);
};

const findTreeNodeByNumericIdAndDepth = (
  nodes: TreeData[],
  targetNumericId: string,
  expectedDepth: number
): TreeData | null => {
  // Tree ids encode their path using "_" separators:
  // e.g. "14_60_233459" where the last segment is the numeric leaf id.
  for (const node of nodes) {
    const nodeNumericId = node.id.split('_').pop();
    if (nodeNumericId === targetNumericId) {
      const nodeDepth = node.id.split('_').length - 1;
      if (nodeDepth === expectedDepth) {
        return node;
      }
    }
    if (node.children) {
      const found = findTreeNodeByNumericIdAndDepth(
        node.children,
        targetNumericId,
        expectedDepth
      );
      if (found) return found;
    }
  }
  return null;
};

export const resolveTreeSelectionFromFilterData = ({
  filterId,
  selectionListId,
  numericId,
  getFilterData,
}: {
  filterId: FilterId;
  selectionListId: string;
  numericId: number;
  getFilterData: (filterId: FilterId) => FilterDataResult;
}): { id: string; name: string } | null => {
  // Only tree filters can be resolved this way.
  const filterDefinition = getFilterDefinition(filterId);
  if (!filterDefinition || filterDefinition.type !== FilterType.TREE) {
    return null;
  }

  const selectionListIds = (filterDefinition as { selectionListIds?: string[] })
    .selectionListIds;
  if (!selectionListIds) return null;

  // biome-ignore lint/complexity/useIndexOf: migrated from develop
  const expectedDepth = selectionListIds.findIndex(
    (id) => id === selectionListId
  );
  if (expectedDepth < 0) return null;

  const treeData = getFilterData(filterId) as TreeData[] | null;
  if (!treeData) return null;

  const foundNode = findTreeNodeByNumericIdAndDepth(
    treeData,
    String(numericId),
    expectedDepth
  );
  if (!foundNode) return null;

  return { id: foundNode.id, name: foundNode.name };
};

export type CompanyRics = {
  rics_k10: number | null;
  rics_k50: number | null;
  rics_k400: number | null;
};

/**
 * Build the path-encoded company tree id `${rics_k10}_${rics_k400}_${rcid}` the
 * COMPANY tree uses (its levels are rics_k10 / rics_k400 / company). Mirrors a
 * company-search pick so `getRoleTaxonomyValue`/`getSkillTaxonomyValue` resolve
 * the adaptive taxonomy from the company's k400 → k50 parent. Returns null when
 * the RICS ancestry is incomplete, so the caller falls back to the bare rcid
 * (taxonomy then stays default, as before this enrichment existed).
 */
export const buildCompanyTreePathId = (
  rcid: number,
  rics: CompanyRics | undefined
): string | null => {
  if (!rics || rics.rics_k10 == null || rics.rics_k400 == null) return null;
  return `${rics.rics_k10}_${rics.rics_k400}_${rcid}`;
};

export const getCompanySelectionListId = (
  kind: PositionCategoryKind
): AllSelectionListsWithSkillTaxonomyKey | null => {
  // Some position categories map to the COMPANY tree filter, but at different levels.
  switch (kind) {
    case PositionCategoryKind.CompanyId:
      return 'company';
    case PositionCategoryKind.RicsK10:
      return 'rics_k10';
    case PositionCategoryKind.RicsK50:
      return 'rics_k50';
    case PositionCategoryKind.RicsK400:
      return 'rics_k400';
    default:
      return null;
  }
};

/**
 * Given a RICS K50 numeric id, look up its K10 parent via the selection lists.
 *
 * The dashboard COMPANY tree only supports rics_k10 / rics_k400 / company levels
 * (see `companyDefinition.selectionListIds`), so K50 results from AI search must
 * be rolled up to their K10 parent before they can be applied as filter chips.
 *
 * The rics_k50 list is not exposed as its own dashboard selection list (only
 * fetched for k10/k50 ancestry on rics_k400/company), so we traverse rics_k400
 * instead — K400 items carry both `rics_k10` and
 * `rics_k50` fields, so any K400 child of this K50 tells us the K10 parent.
 * Returns null when the K10 parent can't be resolved.
 */
const isNumericLike = (value: unknown): value is string | number =>
  typeof value === 'number' ||
  (typeof value === 'string' && /^\d+$/.test(value));

export const rollUpRicsK50ToK10 = ({
  k50NumericId,
  selectionLists,
}: {
  k50NumericId: number;
  selectionLists: SelectionList<ValidValueTypes>[];
}): { numericId: number; name: string | null } | null => {
  const k400List = selectionLists.find((l) => l.id === 'rics_k400');
  if (!k400List) return null;

  const k400Child = k400List.value.find((item) => {
    const k50 = item.rics_k50;
    return isNumericLike(k50) && Number(k50) === k50NumericId;
  });
  if (!k400Child) return null;

  const k10 = k400Child.rics_k10;
  if (!isNumericLike(k10)) return null;

  const k10NumericId = Number(k10);
  if (!Number.isFinite(k10NumericId)) return null;

  const k10Name = resolveSelectionListLabelById({
    selectionListId: 'rics_k10',
    numericId: k10NumericId,
    selectionLists,
  });

  return { numericId: k10NumericId, name: k10Name };
};

export const getRoleSelectionListId = (
  kind: PositionCategoryKind
): AllSelectionListsWithSkillTaxonomyKey | null => {
  // Roles come in multiple granularities; the kind tells us which selection list to use.
  switch (kind) {
    case PositionCategoryKind.RoleK7:
      return 'role_k7';
    case PositionCategoryKind.RoleK150:
      return 'role_k150';
    case PositionCategoryKind.RoleK1500:
      return 'role_k1500';
    case PositionCategoryKind.RoleK15000:
      return 'role_k15000';
    default:
      return null;
  }
};

export const getUserGeoSelectionListId = (
  kind: UserCategoryKind
): AllSelectionListsWithSkillTaxonomyKey | null => {
  // User geography and position geography share the same GEOGRAPHY tree filter, but differ in `isCurrent`.
  switch (kind) {
    case UserCategoryKind.UserRegion:
      return 'region';
    case UserCategoryKind.UserCountry:
      return 'country';
    case UserCategoryKind.UserMsa:
      return 'msa';
    case UserCategoryKind.UserState:
      return 'state';
    case UserCategoryKind.UserCity:
      return 'city';
    default:
      return null;
  }
};

export const getPositionGeoSelectionListId = (
  kind: PositionCategoryKind
): AllSelectionListsWithSkillTaxonomyKey | null => {
  switch (kind) {
    case PositionCategoryKind.Region:
      return 'region';
    case PositionCategoryKind.Country:
      return 'country';
    case PositionCategoryKind.Msa:
      return 'msa';
    case PositionCategoryKind.City:
      return 'city';
    default:
      return null;
  }
};

export const getSkillSelectionListId = (
  kind: UserCategoryKind
): AllSelectionListsWithSkillTaxonomyKey | null => {
  // Skill kinds encode taxonomy depth.
  switch (kind) {
    case UserCategoryKind.SkillLvl_1:
      return 'skill_0';
    case UserCategoryKind.SkillLvl_2:
      return 'skill_1';
    case UserCategoryKind.SkillLvl_3:
      return 'skill_2';
    case UserCategoryKind.SkillLvl_4:
      return 'skill_3';
    default:
      return null;
  }
};

export const resolveStaticListLabelById = ({
  filterId,
  numericId,
}: {
  filterId:
    | FilterId.FLIGHT_RISK
    | FilterId.PRESTIGE
    | FilterId.REMOTE_SUITABILITY;
  numericId: number;
}): string => {
  // Static lists (prestige/flight risk/remote suitability) use a fixed local label map.
  const options = STATIC_FILTER_DATA[filterId] || [];
  const match = options.find((o) => String(o.value) === String(numericId));
  return match?.label || String(numericId);
};
